/**
 * web/screens/worldMapData.ts
 * Pure-constant tables for the world map screen — the five worlds and
 * their per-world stage rosters. Extracted from worldMap.ts to keep the
 * screen file under the 300-line cap (AGENTS.md).
 *
 * @module web/screens/worldMapData
 */

export interface WorldEntry {
  id: number;
  name: string;
  constellation: string;
  route: '/game' | '/space-game' | null;
  accent: string;
}

export interface StageEntry {
  label: string;
  level: string;
  kind: 'main' | 'boss' | 'secret';
}

export const WORLDS: readonly WorldEntry[] = [
  { id: 1, name: 'Sunmeadow Cliffs', constellation: 'Dawn Constellation', route: '/game', accent: '#f5c842' },
  { id: 2, name: 'Starfield Drift', constellation: 'Drift Constellation', route: '/space-game', accent: '#5fb3d4' },
  { id: 3, name: 'Brasscloud Foundry', constellation: 'Forge Constellation', route: '/game', accent: '#c97a3c' },
  { id: 4, name: 'Crystal Tide Ruins', constellation: 'Prism Tide Constellation', route: '/game', accent: '#9c7fd6' },
  { id: 5, name: 'Eclipse Bastion', constellation: 'Crown of Night', route: '/game', accent: '#6e3fa3' },
];

export const STAGES_BY_WORLD: Record<number, readonly StageEntry[]> = {
  1: [
    { label: '1-1', level: '1-1', kind: 'main' },
    { label: '1-2', level: '1-2', kind: 'main' },
    { label: '1-3', level: '1-3', kind: 'main' },
    { label: '1-4', level: '1-4', kind: 'main' },
    { label: 'Secret', level: '1-0', kind: 'secret' },
  ],
  2: [
    { label: '2-1', level: '2-1', kind: 'main' },
    { label: '2-2', level: '2-2', kind: 'main' },
    { label: '2-3', level: '2-3', kind: 'main' },
    { label: '2-4', level: '2-4', kind: 'main' },
    { label: 'Boss', level: '2-B', kind: 'boss' },
    { label: 'Secret', level: '2-S', kind: 'secret' },
  ],
  3: [
    // DECISION: 3-1 (onboarding) + 3-2 (escalation) + 3-3 (Gearling rail
    // set-piece) + 3-4 (boss-approach with SentinelBloom firing line) +
    // 3-B (King Rivet boss arena) + 3-S (exploration secret) shipped as
    // greybox. Further W3 stages are appended here as they land —
    // STAGES_BY_WORLD is the single source of truth for the world-map
    // stage row. The secret stage uses canonical id '3-0' (levelId 0)
    // to match the LevelRegistry numbering convention, same as W1's 1-0.
    { label: '3-1', level: '3-1', kind: 'main' },
    { label: '3-2', level: '3-2', kind: 'main' },
    { label: '3-3', level: '3-3', kind: 'main' },
    { label: '3-4', level: '3-4', kind: 'main' },
    { label: 'Boss', level: '3-B', kind: 'boss' },
    { label: 'Secret', level: '3-0', kind: 'secret' },
  ],
  4: [
    // DECISION: 4-1 (onboarding) + 4-2 (escalation) + 4-3 (Gearling rail
    // set-piece) + 4-4 (boss-approach with SentinelBloom firing line +
    // BrambleRam miniboss) + 4-B (PrismLeviathan boss arena) + 4-S
    // (exploration secret) shipped as greybox. Further W4 stages append
    // here as they land. The secret stage uses canonical id '4-0'
    // (levelId 0) to match the LevelRegistry numbering convention, same
    // as W1's 1-0 and W3's 3-0. worldMap.ts renders this stage row
    // whenever the world is playable; the secret-stage *button* is the
    // only entry skipped until isSecretStageUnlocked (12/12 World 4
    // shards) returns true, so the rest of the row shows immediately.
    { label: '4-1', level: '4-1', kind: 'main' },
    { label: '4-2', level: '4-2', kind: 'main' },
    { label: '4-3', level: '4-3', kind: 'main' },
    { label: '4-4', level: '4-4', kind: 'main' },
    { label: 'Boss', level: '4-B', kind: 'boss' },
    { label: 'Secret', level: '4-0', kind: 'secret' },
  ],
  5: [
    // DECISION: World 5 (Eclipse Bastion) opens with its onboarding stage
    // 5-1 as a greybox, mirroring how 4-1 bootstrapped World 4. The W5
    // card unlocks on the World 4 boss (4-B) clear via isWorldUnlocked in
    // worldUnlock.ts; once unlocked, route '/game' makes the card
    // playable. Later main stages, the StarEater boss arena (5-B), and the
    // secret stage (5-0) append here as they land.
    { label: '5-1', level: '5-1', kind: 'main' },
  ],
};
